import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useModalContext } from "../../components/ModalProvider";
import {
  AnalysisRow,
  ExtractedInstance,
  StoredExtractedObject,
  useDocumentAnalysisAPI,
} from "../../DocumentAnalysisAPI";
import InstanceCards from "./InstanceCards";
import { StatusBadge } from "./analysisView";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * One stored run, read back.
 *
 * The same cards the processing test shows while a run happens, over rows out of the database
 * instead of events off a stream. Deliberately the same component: a stored result that looked
 * different from a live one would invite the question of whether it *is* different, and the answer
 * has to be visibly no.
 *
 * Read-only, with no action on it. What could be done here — bind an object to something in the
 * estate, reject an extraction — belongs to the resolution step, which is not built.
 */

/** A stored row, in the shape the cards read. The two differ only by the database's own fields. */
const asInstance = (stored: StoredExtractedObject): ExtractedInstance => ({
  term: stored.term,
  className: stored.className,
  attributes: stored.attributes ?? {},
  unmappedAttributes: stored.unmappedAttributes,
  sourceRef: stored.sourceRef ?? { quote: "", location: "" },
  confidence: stored.confidence,
});

export default function AnalysisDetail({
  analysisId,
  onClose,
}: {
  analysisId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const api = useDocumentAnalysisAPI();

  const [analysis, setAnalysis] = useState<AnalysisRow | null>(null);
  const [objects, setObjects] = useState<StoredExtractedObject[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!analysisId) {
      setAnalysis(null);
      setObjects([]);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const result = await api.getAnalysis(analysisId);
        setAnalysis(result.analysis);
        setObjects(result.extractedObjects ?? []);
      } catch (error) {
        pushError(error);
        onClose();
      } finally {
        setLoading(false);
      }
    };

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  const subtype = [analysis?.subtypeName, analysis?.parameterValue].filter(Boolean).join(": ");

  return (
    <Dialog open={!!analysisId} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="truncate">{analysis?.document?.fileName ?? "…"}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col gap-2">
            {[0, 1, 2].map((row) => (
              <Skeleton key={row} className="h-16 w-full" />
            ))}
          </div>
        ) : analysis ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <StatusBadge status={analysis.status} failedStep={analysis.failedStep} />

              {analysis.roleName ? (
                <span>
                  <span className="font-semibold">{analysis.roleName}</span>
                  {subtype ? <span className="text-muted-foreground"> → {subtype}</span> : null}
                </span>
              ) : (
                <span className="text-muted-foreground">{t("documentAnalysisPanel.test.undecided")}</span>
              )}

              <span className="font-mono text-xs text-muted-foreground">
                {typeof analysis.roleConfidence === "number" ? analysis.roleConfidence.toFixed(2) : "—"} /{" "}
                {typeof analysis.subtypeConfidence === "number"
                  ? analysis.subtypeConfidence.toFixed(2)
                  : "—"}
              </span>
            </div>

            {/* What decided this run, and against which state of the catalogue — the two facts that
                make two runs over the same document comparable at all. */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>{new Date(analysis.startedAt).toLocaleString("de-DE")}</span>
              {analysis.provenance?.roleGeneration ? (
                <span>
                  {t("documentAnalysisPanel.analyses.generation")} {analysis.provenance.roleGeneration}
                </span>
              ) : null}
              {analysis.provenance?.llmModel ? <span>{analysis.provenance.llmModel}</span> : null}
              {analysis.document?.convertedFormat ? (
                <span>
                  {t(`documentAnalysisPanel.test.format.${analysis.document.convertedFormat}`)}
                  {analysis.document.charCount
                    ? ` · ${t("documentAnalysisPanel.test.chars", { count: analysis.document.charCount })}`
                    : ""}
                </span>
              ) : null}
              <span className="font-mono">{analysis.document?.contentHash?.slice(0, 23)}…</span>
            </div>

            <InstanceCards payload={{ instances: objects.map(asInstance) }} />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
